import type { LegendListRef } from '@legendapp/list/react-native';
import { useRef } from 'react';
import { useQuery, useSubscription } from 'urql';
import Box from '@/components/Unistyles/Box';
import { graphql, type ResultOf } from '@/gql/graphql';
import type { BadgeAssigneeBoolExp, BadgeBoolExp } from '@/gql/scalars';
import useDebounce from '@/hooks/useDebounce';
import usePaginatedList from '@/hooks/usePaginatedList';
import Paginator from '../Custom/Paginator';
import List, { type ListProps } from '../Unistyles/List';
import { BadgeListItemFragment } from './BadgeListItem';

const BADGE_LIMIT = 25;
const ASSIGNMENT_LIMIT = 5;
const EMPTY_BADGE_ASSIGNEES_WHERE: BadgeAssigneeBoolExp = {};

const GetBadgeList = graphql(/* GraphQL */ `
  query GetBadgeList(
    $includeBadgeAssignees: Boolean!
    $badgeAssigneesWhere: badge_assignee_bool_exp = {}
    $badgeWhere: badge_bool_exp = {}
    $eventId: uuid
    $teamId: uuid
    $badgeLimit: Int!
    $badgeOffset: Int!
  ) {
    relevant_badge(
      where: $badgeWhere
      args: { event_id: $eventId, team_id: $teamId }
      limit: $badgeLimit
      offset: $badgeOffset
      order_by: { title: asc }
    ) {
      id
      ...BadgeListItem
    }
    relevant_badge_aggregate(args: { event_id: $eventId, team_id: $teamId }) {
      aggregate {
        count
      }
    }
  }
`, [BadgeListItemFragment]);

type BadgeListRow = NonNullable<
  NonNullable<ResultOf<typeof GetBadgeList>['relevant_badge']>[number]
>;

const WatchBadgeList = graphql(/* GraphQL */ `
  subscription WatchBadgeList(
    $includeBadgeAssignees: Boolean!
    $badgeAssigneesWhere: badge_assignee_bool_exp = {}
    $badgeWhere: badge_bool_exp = {}
    $eventId: uuid
    $teamId: uuid
    $badgeLimit: Int!
    $badgeOffset: Int!
  ) {
    relevant_badge(
      where: $badgeWhere
      args: { event_id: $eventId, team_id: $teamId }
      limit: $badgeLimit
      offset: $badgeOffset
      order_by: { title: asc }
    ) {
      id
      ...BadgeListItem
    }
  }
`, [BadgeListItemFragment]);

export default function BadgeList({
  badgeAssigneesWhere = EMPTY_BADGE_ASSIGNEES_WHERE,
  badgeWhere,
  eventId,
  teamId,
  ...props
}: {
  badgeAssigneesWhere?: BadgeAssigneeBoolExp;
  badgeWhere?: BadgeBoolExp;
  eventId?: string | null;
  teamId?: string | null;
} & Omit<ListProps<BadgeListRow>, 'data'>) {
  const { debouncedValue } = useDebounce();
  const listRef = useRef<LegendListRef>(null);
  const paginated = usePaginatedList({ limit: BADGE_LIMIT, listRef });
  const { limit, offset } = paginated;

  const variables = {
    badgeLimit: limit,
    badgeOffset: debouncedValue.length ? 0 : offset,
    badgeAssignmentsLimit: ASSIGNMENT_LIMIT,
    badgeAssigneesWhere,
    eventId,
    teamId,
    includeBadgeAssignees: !!badgeAssigneesWhere,
  };

  const [{ data, fetching, error }, refresh] = useQuery({
    query: GetBadgeList,
    variables,
  });

  useSubscription({
    query: WatchBadgeList,
    variables,
  });

  const badges = data?.relevant_badge || [];
  const badgeCount = data?.relevant_badge_aggregate.aggregate?.count || 0;

  return (
    <Box flex>
      <List
        {...props}
        ref={listRef}
        data={badges}
        fetching={fetching}
        error={error}
        onRetry={() => refresh({ requestPolicy: 'network-only' })}
        ListFooterComponent={
          badgeCount > paginated.limit ? (
            <Paginator {...paginated} total={badgeCount} />
          ) : null
        }
        keyExtractor={(item) => item.id}
      />
    </Box>
  );
}
